import { computed, ref, watch } from 'vue'
import type { PreapprovedMember } from '~/shared/types/preapprovedMember.js'

/**
 * Centralized composable for working with member pre-approvals.
 * Handles loading preapprovals for the current user and exposes helper utilities.
 */
export function useMemberPreapprovals() {
  const preapprovedStore = usePreapprovedMembersStore()
  const subkrewesStore = useSubKrewesStore()
  const membersStore = useMembersStore()
  const currentUser = useCurrentUser()

  const preapprovals = ref<PreapprovedMember[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const loadedEmail = ref<string | null>(null)
  const attemptedSubkrewesLoad = ref(false)

  // Helper to access current member (may be undefined early in lifecycle)
  const currentMember = computed<Member | null>(() => {
    const uid = currentUser.value?.uid
    if (!uid) return null
    return membersStore.members?.[`members/${uid}`] || null
  })

  async function load(force = false) {
    try {
      if (loading.value) return
      const email = currentUser.value?.email?.toLowerCase().trim()
      if (!email) return
      if (!force && loadedEmail.value === email && preapprovals.value.length > 0) return
      loading.value = true
      error.value = null
      const result = await preapprovedStore.findPreapprovalsByEmail(email)
      preapprovals.value = result
      loadedEmail.value = email
    }
    catch (e: unknown) {
      console.error('[useMemberPreapprovals] load error', e)
      const message = typeof e === 'object' && e && 'message' in e ? String((e as { message: unknown }).message) : 'Failed to load pre-approvals'
      error.value = message
    }
    finally {
      loading.value = false
    }
  }

  // Auto-load when user email becomes available
  watch(() => currentUser.value?.email, (email) => {
    if (email) load()
  }, { immediate: true })

  // Ensure subkrewes are loaded if the user does not yet belong to a subkrewe.
  // This supports showing names for preapproved (and especially sponsored) subkrewes
  // in contexts (like dues dialog) that appear before BrowserView triggers subscriptions.
  watch([
    currentMember,
    () => preapprovals.value.length,
  ], async ([member]) => {
    if (attemptedSubkrewesLoad.value) return
    // Only load if: we have a member object, they have no subkrewe, store empty/uninitialized
    if (member && !member.subkrewe && Object.keys(subkrewesStore.subkrewes || {}).length === 0) {
      attemptedSubkrewesLoad.value = true
      try {
        await subkrewesStore.ensureSubKrewesLoaded()
      }
      catch (e) {
        console.warn('[useMemberPreapprovals] Failed to pre-load subkrewes for member without subkrewe', e)
      }
    }
  }, { immediate: true })

  function isPreapprovedForSubkrewe(subkrewe: SubKrewe | string | null | undefined): boolean {
    if (!subkrewe) return false
    const id = typeof subkrewe === 'string' ? subkrewe : subkrewe.id
    return preapprovals.value.some(p => p.subkreweId === id)
  }

  // Compute full SubKrewe objects for preapproved list (when available in store)
  // Explicitly reference subkrewesStore.subkrewes to guarantee reactivity when the store populates
  const preapprovedSubkrewes = computed<SubKrewe[]>(() => {
    // Force dependency on the entire record so population triggers recompute
    const _dep = subkrewesStore.subkrewes
    if (!preapprovals.value.length) return []
    const getterFn = subkrewesStore.getSubkreweById as unknown as ((id: string) => SubKrewe | undefined)
    if (typeof getterFn !== 'function') return []
    return preapprovals.value
      .map(p => getterFn(p.subkreweId))
      .filter((sk): sk is SubKrewe => !!sk)
  })

  const sponsoredPreapprovedSubkrewes = computed(() => preapprovedSubkrewes.value.filter(sk => sk.sponsored === true))
  const bandPreapprovedSubkrewes = computed(() => preapprovedSubkrewes.value.filter(sk => sk.music === 'band'))

  return {
    preapprovals,
    loading,
    error: computed(() => error.value),
    load,
    isPreapprovedForSubkrewe,
    preapprovedSubkrewes,
    sponsoredPreapprovedSubkrewes,
    bandPreapprovedSubkrewes,
  }
}
